import type { ExerciseTemplate } from '@yuan-math/exercises';
import { choiceExercise, numericExercise, options, template, trap } from './authoring.ts';

/**
 * Proof, parallel lines and triangle congruence.
 *
 * Much of this is multiple choice, and deliberately so. The skill being tested
 * is *whether a conclusion follows*, and a numeric answer box cannot ask that
 * question — the wrong reasoning and the right reasoning often reach the same
 * number.
 */
export const GEOMETRY_PROOF_TEMPLATES: ExerciseTemplate[] = [
  template({
    id: 'proof-converse',
    conceptId: 'geometric-proof',
    description: 'Distinguish a statement from its converse.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const pair = rng.pick([
        { claim: 'every square is a rectangle', converse: 'every rectangle is a square', truth: false },
        { claim: 'if a triangle is equilateral then it is isosceles', converse: 'if a triangle is isosceles then it is equilateral', truth: false },
        { claim: 'if two angles are vertically opposite then they are equal', converse: 'if two angles are equal then they are vertically opposite', truth: false },
      ]);

      const { choices, correctId } = options(
        [
          { label: 'No — the converse is a different claim, and this one is false', correct: true },
          { label: 'Yes — a true statement makes its converse true', misconceptionId: 'converse-assumed-true' },
          { label: 'Yes, provided the figure is drawn accurately', misconceptionId: 'measurement-taken-as-proof' },
          { label: 'Yes, because both mention the same shapes', misconceptionId: 'converse-assumed-true' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: `It is true that ${pair.claim}. Does it follow that ${pair.converse}?`,
        choices,
        correctId,
        summary: `Reversing a true statement does not preserve its truth. The claim and its converse have to be argued separately.`,
        hint: 'Try to find a single example that satisfies the second statement but not the first.',
      });
    },
  }),

  template({
    id: 'proof-what-justifies',
    conceptId: 'geometric-proof',
    description: 'Choose the reason that justifies a step.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const step = rng.pick([
        {
          claim: 'Two angles on a straight line are known to be 3x and 2x. Why does 3x + 2x = 180?',
          reason: 'Angles on a straight line add to 180°',
          wrong: 'Because the two angles look about equal',
        },
        {
          claim: 'Two lines cross. Why is the angle opposite a 40° angle also 40°?',
          reason: 'Vertically opposite angles are equal',
          wrong: 'Because the diagram is drawn to scale',
        },
        {
          claim: 'A triangle has angles 50°, 60° and x. Why does x = 70?',
          reason: 'The angles of a triangle add to 180°',
          wrong: 'Because measuring it gives about 70°',
        },
      ]);

      const { choices, correctId } = options(
        [
          { label: step.reason, correct: true },
          { label: step.wrong, misconceptionId: 'measurement-taken-as-proof' },
          { label: 'It is true by definition and needs no reason', misconceptionId: 'measurement-taken-as-proof' },
          { label: 'Because the converse of the statement holds', misconceptionId: 'converse-assumed-true' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: step.claim,
        choices,
        correctId,
        summary: `${step.reason}. A proof cites a result that holds for every such figure, never a measurement of one of them.`,
        hint: 'A reason has to be something known to be true of *all* such figures.',
      });
    },
  }),

  template({
    id: 'logic-name-the-rearrangement',
    conceptId: 'conditional-statements',
    description: 'Name the converse, inverse or contrapositive of a conditional.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      /*
       * Subject and predicate are stored apart because these four sentences are
       * built by *swapping* the halves. Writing each half as a whole clause —
       * "a shape is a square" and "it has four equal sides" — produces "If it
       * has four equal sides, then a shape is a square" the moment the swap
       * happens, and a question that reads like that teaches nothing about
       * logic and everything about generated content.
       */
      const claim = rng.pick([
        { subject: 'a quadrilateral', pa: 'is a square', notPa: 'is not a square', pb: 'has four equal sides', notPb: 'does not have four equal sides' },
        { subject: 'a number', pa: 'is a multiple of 4', notPa: 'is not a multiple of 4', pb: 'is even', notPb: 'is odd' },
        { subject: 'a triangle', pa: 'is equilateral', notPa: 'is not equilateral', pb: 'is isosceles', notPb: 'is not isosceles' },
        { subject: 'a quadrilateral', pa: 'is a rhombus', notPa: 'is not a rhombus', pb: 'is a parallelogram', notPb: 'is not a parallelogram' },
      ]);
      const wanted = rng.pick(['converse', 'inverse', 'contrapositive'] as const);

      const forms = {
        converse: `If ${claim.subject} ${claim.pb}, then it ${claim.pa}.`,
        inverse: `If ${claim.subject} ${claim.notPa}, then it ${claim.notPb}.`,
        contrapositive: `If ${claim.subject} ${claim.notPb}, then it ${claim.notPa}.`,
      };
      const wrongFor = {
        converse: [
          { label: forms.contrapositive, misconceptionId: 'contrapositive-confused-with-converse' },
          { label: forms.inverse, misconceptionId: 'inverse-assumed-true' },
        ],
        inverse: [
          { label: forms.contrapositive, misconceptionId: 'contrapositive-confused-with-converse' },
          { label: forms.converse, misconceptionId: 'inverse-assumed-true' },
        ],
        contrapositive: [
          { label: forms.converse, misconceptionId: 'contrapositive-confused-with-converse' },
          { label: forms.inverse, misconceptionId: 'inverse-assumed-true' },
        ],
      }[wanted];

      const { choices, correctId } = options(
        [
          { label: forms[wanted], correct: true },
          ...wrongFor,
          { label: `If ${claim.subject} ${claim.pa}, then it ${claim.pb}.`, misconceptionId: 'contrapositive-confused-with-converse' },
        ],
        rng.shuffle,
      );

      const how = {
        converse: 'swapping the two halves',
        inverse: 'negating both halves',
        contrapositive: 'swapping the two halves and negating both',
      }[wanted];

      return choiceExercise({
        prompt: `Take the statement: "If ${claim.subject} ${claim.pa}, then it ${claim.pb}." Which of these is its ${wanted}?`,
        choices,
        correctId,
        summary: `The ${wanted} is built by ${how}, which gives "${forms[wanted]}"`,
        hint: 'Converse: swap. Inverse: negate. Contrapositive: swap and negate.',
      });
    },
  }),

  template({
    id: 'logic-which-follows',
    conceptId: 'conditional-statements',
    description: 'Decide which rearrangement of a true statement is guaranteed to be true.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const claim = rng.pick([
        { subject: 'a quadrilateral', pa: 'is a square', notPa: 'is not a square', pb: 'is a rectangle', notPb: 'is not a rectangle' },
        { subject: 'a number', pa: 'is a multiple of 6', notPa: 'is not a multiple of 6', pb: 'is a multiple of 3', notPb: 'is not a multiple of 3' },
        { subject: 'a triangle', pa: 'is equilateral', notPa: 'is not equilateral', pb: 'is isosceles', notPb: 'is not isosceles' },
        { subject: 'a quadrilateral', pa: 'is a rhombus', notPa: 'is not a rhombus', pb: 'is a parallelogram', notPb: 'is not a parallelogram' },
      ]);

      const { choices, correctId } = options(
        [
          { label: `If ${claim.subject} ${claim.notPb}, then it ${claim.notPa} — the contrapositive`, correct: true },
          { label: `If ${claim.subject} ${claim.pb}, then it ${claim.pa} — the converse`, misconceptionId: 'converse-assumed-true' },
          { label: `If ${claim.subject} ${claim.notPa}, then it ${claim.notPb} — the inverse`, misconceptionId: 'inverse-assumed-true' },
          { label: 'All three follow, because they are built from a true statement', misconceptionId: 'inverse-assumed-true' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: `It is true that if ${claim.subject} ${claim.pa}, then it ${claim.pb}. Which rearrangement is guaranteed to be true as well?`,
        choices,
        correctId,
        summary:
          'Only the contrapositive comes free. It says the same thing as the original statement, while the converse and the inverse are different claims that would each need their own argument.',
        hint: 'One of the three says exactly what the original statement says, in other words.',
      });
    },
  }),

  template({
    id: 'logic-counterexample',
    conceptId: 'conditional-statements',
    description: 'Refute a universal claim with a single counterexample.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const item = rng.pick([
        { claim: 'every rectangle is a square', example: 'a rectangle measuring 3 by 5' },
        { claim: 'every even number is a multiple of 4', example: 'the number 6' },
        { claim: 'two triangles with equal angles are congruent', example: 'a triangle and a photograph of it enlarged twice' },
        { claim: 'every quadrilateral with perpendicular diagonals is a square', example: 'a kite with unequal diagonals' },
      ]);

      const { choices, correctId } = options(
        [
          { label: `One case where it fails — ${item.example}`, correct: true },
          { label: 'Several cases where it fails, to be sure it was not a fluke', misconceptionId: 'counterexample-count-misjudged' },
          { label: 'A careful drawing showing the claim does not hold', misconceptionId: 'measurement-taken-as-proof' },
          { label: 'A proof that the converse is false', misconceptionId: 'converse-assumed-true' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: `What is needed to show that the claim "${item.claim}" is false?`,
        choices,
        correctId,
        summary: `A claim about every case is destroyed by one case that fails — ${item.example} does it. A second counterexample adds nothing.`,
        hint: 'How many exceptions does a statement about *all* cases need before it stops being true?',
      });
    },
  }),

  template({
    id: 'logic-inductive-or-deductive',
    conceptId: 'conditional-statements',
    description: 'Tell inductive reasoning from deductive reasoning.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const item = rng.pick([
        { text: 'I measured the angles of eleven triangles and they all totalled 180°, so every triangle does', kind: 'inductive' as const },
        { text: 'The first six terms went up by 3 each time, so the rule must be add 3', kind: 'inductive' as const },
        { text: 'Angles on a straight line total 180°, and one of these is 55°, so the other is 125°', kind: 'deductive' as const },
        { text: 'All radii of a circle are equal, so this triangle with two radii as sides is isosceles', kind: 'deductive' as const },
      ]);
      const answer = item.kind === 'inductive' ? 'Inductive — it argues from examples to a general rule' : 'Deductive — it argues from accepted statements to a forced conclusion';
      const wrong =
        item.kind === 'inductive'
          ? 'Deductive — it argues from accepted statements to a forced conclusion'
          : 'Inductive — it argues from examples to a general rule';

      const { choices, correctId } = options(
        [
          { label: answer, correct: true },
          { label: wrong, misconceptionId: 'inductive-taken-as-deductive' },
          { label: 'Neither — it is a definition and needs no reasoning', misconceptionId: 'inductive-taken-as-deductive' },
          { label: 'Both, since every proof begins with examples', misconceptionId: 'inductive-taken-as-deductive' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: `"${item.text}." What kind of reasoning is this?`,
        choices,
        correctId,
        summary:
          item.kind === 'inductive'
            ? 'Reasoning from observed cases to a general rule is inductive. It is how a conjecture is found, and it can be wrong.'
            : 'Reasoning from statements already accepted to a conclusion they force is deductive. That is what a proof is made of.',
        hint: 'Did the argument start from examples, or from something already established?',
      });
    },
  }),

  template({
    id: 'logic-hypothesis-conclusion',
    conceptId: 'conditional-statements',
    description: 'Identify the hypothesis of a conditional, and what it does not claim.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const claim = rng.pick([
        { subject: 'a quadrilateral', pa: 'is a parallelogram', pb: 'has equal opposite angles', other: 'a trapezium' },
        { subject: 'a triangle', pa: 'is right-angled', pb: 'obeys the Pythagorean theorem', other: 'an acute triangle' },
        { subject: 'a number', pa: 'ends in 0', pb: 'is a multiple of 5', other: 'the number 35' },
        { subject: 'a quadrilateral', pa: 'is a rhombus', pb: 'has diagonals meeting at right angles', other: 'a rectangle' },
      ]);

      const { choices, correctId } = options(
        [
          { label: 'Nothing at all — the statement never mentions that case', correct: true },
          { label: 'That the conclusion fails, since the hypothesis does', misconceptionId: 'inverse-assumed-true' },
          { label: 'That the conclusion still holds, since conditionals work both ways', misconceptionId: 'converse-assumed-true' },
          { label: 'That the statement is false, because a case escapes it', misconceptionId: 'counterexample-count-misjudged' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: `Take: "If ${claim.subject} ${claim.pa}, then it ${claim.pb}." What does this statement tell you about ${claim.other}?`,
        choices,
        correctId,
        summary: `A conditional makes a claim only about cases satisfying its hypothesis. ${claim.other.charAt(0).toUpperCase()}${claim.other.slice(1)} fails it, so the statement is silent — that case has not been ruled out, it has simply not been mentioned.`,
        hint: 'Does "if A then B" say anything whatever about something that is not an A?',
      });
    },
  }),

  template({
    id: 'parallel-angle-value',
    conceptId: 'parallel-lines',
    description: 'Find an angle made by a transversal crossing parallel lines.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const given = rng.int(35, 145);
      const relation = rng.pick(
        difficulty === 2
          ? (['corresponding', 'alternate'] as const)
          : (['corresponding', 'alternate', 'co-interior'] as const),
      );
      const answer = relation === 'co-interior' ? 180 - given : given;
      const wording = {
        corresponding: 'in corresponding positions',
        alternate: 'alternate angles',
        'co-interior': 'co-interior angles, on the same side of the transversal',
      }[relation];

      return numericExercise({
        prompt: `A transversal crosses two parallel lines. One angle is ${given}°. What is the size of the angle that is ${wording}?`,
        unit: 'degrees',
        figure: { kind: 'parallel-lines', mark: relation === 'corresponding' ? [1, 5] : relation === 'alternate' ? [4, 6] : [3, 6] },
        value: answer,
        summary:
          relation === 'co-interior'
            ? `Co-interior angles add to 180°, so the other is 180 − ${given} = ${answer}°.`
            : `${relation === 'corresponding' ? 'Corresponding' : 'Alternate'} angles are equal, so the other is also ${answer}°.`,
        hint: 'Corresponding and alternate angles are equal; co-interior angles add to 180°.',
        steps: [
          {
            label: relation === 'co-interior' ? 'Co-interior angles are supplementary.' : 'These angles are equal.',
            latex: relation === 'co-interior' ? `180 - ${given} = ${answer}` : `${answer}`,
          },
        ],
        traps: [
          ...(relation === 'co-interior'
            ? [trap(given, 'co-interior-taken-equal')]
            : [trap(180 - given, 'angle-pair-misnamed')]),
        ],
      });
    },
  }),

  template({
    id: 'parallel-angle-name',
    conceptId: 'parallel-lines',
    description: 'Name the relationship between a marked pair of angles.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const relation = rng.pick(['corresponding', 'alternate', 'co-interior'] as const);
      const mark: [number, number] = relation === 'corresponding' ? [1, 5] : relation === 'alternate' ? [4, 6] : [3, 6];
      const label = {
        corresponding: 'Corresponding — equal',
        alternate: 'Alternate — equal',
        'co-interior': 'Co-interior — supplementary',
      };

      const { choices, correctId } = options(
        [
          { label: label[relation], correct: true },
          {
            label: relation === 'co-interior' ? label.alternate : label['co-interior'],
            misconceptionId: relation === 'co-interior' ? 'co-interior-taken-equal' : 'angle-pair-misnamed',
          },
          {
            label: relation === 'corresponding' ? label.alternate : label.corresponding,
            misconceptionId: 'angle-pair-misnamed',
          },
          { label: 'Vertically opposite — equal', misconceptionId: 'angle-pair-misnamed' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: `The two marked angles are made by a transversal crossing parallel lines. What is their relationship?`,
        figure: { kind: 'parallel-lines', mark },
        choices,
        correctId,
        summary:
          relation === 'co-interior'
            ? 'They lie between the parallels on the same side of the transversal, so they add to 180°.'
            : relation === 'corresponding'
              ? 'They sit in the same corner at each crossing, so they are equal.'
              : 'They lie between the parallels on opposite sides of the transversal, so they are equal.',
        hint: 'Where do the two angles sit relative to the transversal — same side, or opposite sides?',
      });
    },
  }),

  template({
    id: 'congruence-criterion',
    conceptId: 'triangle-congruence',
    description: 'Decide whether the given information proves congruence.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const scenario = rng.pick([
        { given: 'all three pairs of sides are equal', works: true, name: 'SSS' },
        { given: 'two pairs of sides and the angle between them are equal', works: true, name: 'SAS' },
        { given: 'two pairs of angles and the side between them are equal', works: true, name: 'ASA' },
        { given: 'two pairs of sides and an angle that is not between them are equal', works: false, name: 'SSA' },
        { given: 'all three pairs of angles are equal', works: false, name: 'AAA' },
      ]);

      const { choices, correctId } = options(
        [
          {
            label: scenario.works
              ? `Yes — this is ${scenario.name}, a valid criterion`
              : scenario.name === 'AAA'
                ? 'No — this makes them similar, but not necessarily the same size'
                : 'No — two different triangles can be built from this information',
            correct: true,
          },
          {
            label: scenario.works ? 'No — three measurements are never enough' : `Yes — this is ${scenario.name}, a valid criterion`,
            misconceptionId: scenario.name === 'AAA' ? 'angles-alone-give-congruence' : 'ssa-accepted-as-congruence',
          },
          { label: 'Only if the triangles are also right-angled', misconceptionId: 'ssa-accepted-as-congruence' },
          { label: 'Only if the triangles are drawn the same way up', misconceptionId: 'measurement-taken-as-proof' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: `In two triangles, ${scenario.given}. Does that prove the triangles are congruent?`,
        choices,
        correctId,
        summary: scenario.works
          ? `Yes. ${scenario.name} fixes the triangle completely — the remaining three measurements are forced.`
          : scenario.name === 'AAA'
            ? 'No. Equal angles fix the shape but not the size, which is similarity rather than congruence.'
            : 'No. With the angle outside the two sides, two genuinely different triangles fit the same measurements.',
        hint: 'Ask whether you could build two different triangles from exactly this information.',
      });
    },
  }),

  template({
    id: 'triangle-inequality',
    conceptId: 'triangle-properties',
    description: 'Decide whether three lengths can form a triangle.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const possible = rng.bool();
      const a = rng.int(3, 9);
      const b = rng.int(3, 9);
      // Either strictly inside the range, or well outside it.
      const c = possible ? rng.int(Math.abs(a - b) + 1, a + b - 1) : a + b + rng.int(1, 4);

      const { choices, correctId } = options(
        [
          {
            label: possible ? 'Yes — any two sides together exceed the third' : 'No — two of the sides are too short to reach',
            correct: true,
          },
          {
            label: possible ? 'No — two of the sides are too short to reach' : 'Yes — any two sides together exceed the third',
            misconceptionId: 'triangle-inequality-ignored',
          },
          { label: 'Only if the triangle is right-angled', misconceptionId: 'triangle-inequality-ignored' },
          { label: 'It cannot be decided without the angles', misconceptionId: 'triangle-inequality-ignored' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: `Can a triangle have sides of ${a}, ${b} and ${c}?`,
        choices,
        correctId,
        summary: possible
          ? `Every pair adds to more than the remaining side, so the sides can meet.`
          : `${a} + ${b} = ${a + b}, which is not more than ${c}. Those two sides cannot reach far enough to meet.`,
        hint: 'Add the two shortest sides. Is the total longer than the longest side?',
      });
    },
  }),

  template({
    id: 'isosceles-angle',
    conceptId: 'triangle-properties',
    description: 'Use the base angles of an isosceles triangle.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const apex = rng.int(20, 140);
      const base = (180 - apex) / 2;
      // Keep the arithmetic exact; a half-degree answer teaches nothing extra.
      const apexEven = apex % 2 === 0 ? apex : apex + 1;
      const baseAngle = (180 - apexEven) / 2;

      return numericExercise({
        prompt: `An isosceles triangle has an apex angle of ${apexEven}°. How big is each base angle?`,
        unit: 'degrees',
        figure: { kind: 'shape', shape: 'triangle', labels: { base: 'the two base angles are equal' } },
        value: baseAngle,
        summary: `The two base angles are equal and share what is left: (180 − ${apexEven}) ÷ 2 = ${baseAngle}°.`,
        hint: 'The two equal sides are opposite the two equal angles. What is left of the 180°, shared between them?',
        steps: [
          { label: 'Subtract the apex from 180°.', latex: `180 - ${apexEven} = ${180 - apexEven}` },
          { label: 'Share it between the two equal base angles.', latex: `${180 - apexEven} \\div 2 = ${baseAngle}` },
        ],
        traps: [
          ...(180 - apexEven === baseAngle ? [] : [trap(180 - apexEven, 'polygon-angle-sum-wrong')]),
          ...(base === baseAngle && apexEven === baseAngle ? [] : [trap(apexEven, 'angle-pair-misnamed')]),
        ],
      });
    },
  }),

  /* --------------------------------------------------------- constructions */
  template({
    id: 'construction-identify',
    conceptId: 'constructions',
    description: 'Name the construction a set of compass arcs produces.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const cases = [
        {
          recipe:
            'The compass point is put on A and an arc swung; keeping the same opening, the point is put on B and a second arc swung. The two crossings are joined.',
          answer: 'the perpendicular bisector of AB',
          figure: 'perpendicular-bisector' as const,
        },
        {
          recipe:
            'An arc centred on the vertex cuts both arms of the angle. From each of those two points, equal arcs are swung, and the vertex is joined to where they cross.',
          answer: 'the bisector of the angle',
          figure: 'angle-bisector' as const,
        },
        {
          recipe:
            'The compass is opened to the whole length of AB. An arc is swung from A and another from B, and both ends are joined to where they cross.',
          answer: 'an equilateral triangle on AB',
          figure: 'equilateral-triangle' as const,
        },
      ];
      const chosen = rng.pick(cases);
      const wrong = cases.filter((entry) => entry.answer !== chosen.answer);

      const { choices, correctId } = options(
        [
          { label: chosen.answer, correct: true },
          ...wrong.map((entry) => ({ label: entry.answer, misconceptionId: 'construction-purpose-confused' })),
          { label: 'a line through A parallel to AB', misconceptionId: 'construction-purpose-confused' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: `${chosen.recipe} What has been constructed?`,
        figure: { kind: 'construction', construction: chosen.figure, showArcs: false },
        choices,
        correctId,
        summary: `${chosen.answer.charAt(0).toUpperCase()}${chosen.answer.slice(1)}. Where the compass point is placed, and whether the opening changes between arcs, is what decides the result.`,
        hint: 'Ask what every point on each arc has in common: they are all the same distance from wherever the compass point stood.',
      });
    },
  }),

  template({
    id: 'construction-justifies',
    conceptId: 'constructions',
    description: 'Give the reason a construction is exact rather than approximate.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const item = rng.pick([
        {
          question:
            'Arcs of equal radius are drawn from A and from B, meeting at P. Why must P be exactly the same distance from A as from B?',
          reason: 'Both arcs were drawn with the same compass opening, so PA and PB are the same length by construction',
          figure: 'perpendicular-bisector' as const,
        },
        {
          question:
            'In the perpendicular bisector construction the arcs meet at P and Q. Why is PQ perpendicular to AB, rather than merely close to it?',
          reason: 'APBQ is a rhombus, and SSS proves the two triangles congruent, so the angles at the crossing are equal — and equal angles on a line are 90°',
          figure: 'perpendicular-bisector' as const,
        },
        {
          question:
            'In the angle bisector construction, why are the two halves exactly equal?',
          reason: 'The two triangles formed share a side and have two pairs of equal radii, so SSS makes them congruent and the angles correspond',
          figure: 'angle-bisector' as const,
        },
      ]);

      const { choices, correctId } = options(
        [
          { label: item.reason, correct: true },
          { label: 'Because a protractor confirms it in every case anyone has drawn', misconceptionId: 'measurement-taken-as-proof' },
          { label: 'Because the figure is drawn accurately enough for the difference not to matter', misconceptionId: 'measurement-taken-as-proof' },
          { label: 'Because the two angles look the same size', misconceptionId: 'measurement-taken-as-proof' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: item.question,
        figure: { kind: 'construction', construction: item.figure },
        choices,
        correctId,
        summary:
          'A construction is exact because a compass makes equal lengths by definition, and congruence turns those equal lengths into an equal angle. That is the whole difference between constructing and drawing.',
        hint: 'The compass has not been reopened. What does that guarantee about the lengths, and which congruence criterion uses lengths alone?',
      });
    },
  }),

  template({
    id: 'construction-arc-radius',
    conceptId: 'constructions',
    description: 'Decide which compass openings make the perpendicular bisector construction work.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const length = rng.int(3, 12) * 2;
      const half = length / 2;
      const tooSmall = rng.int(1, half - 1);
      const works = rng.int(half + 1, half + 6);

      const { choices, correctId } = options(
        [
          { label: `${works} cm — the arcs cross, and any opening over ${half} cm would do`, correct: true },
          { label: `${tooSmall} cm — a smaller opening keeps the drawing tidy`, misconceptionId: 'construction-arcs-too-small' },
          { label: `exactly ${half} cm, so the arcs meet at the midpoint`, misconceptionId: 'construction-arcs-too-small' },
          { label: `${works} cm from A and ${tooSmall} cm from B`, misconceptionId: 'construction-purpose-confused' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: `AB is ${length} cm long, and you are constructing its perpendicular bisector. Which compass setting works?`,
        figure: { kind: 'construction', construction: 'perpendicular-bisector' },
        choices,
        correctId,
        summary: `Anything wider than half of ${length} cm works. Opened to less the arcs never meet; opened to exactly ${half} cm they touch at one point only, which gives no second point to draw a line through.`,
        hint: 'How far can a point on the arc from A be from the midpoint? And how far must the two arcs reach to meet at all?',
      });
    },
  }),

  template({
    id: 'construction-locus',
    conceptId: 'constructions',
    description: 'Match a set of equidistant points to the construction that draws it.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const item = rng.pick([
        {
          description: 'all the points equidistant from two fixed points A and B',
          answer: 'the perpendicular bisector of AB',
          other: 'the bisector of the angle between them',
          figure: 'perpendicular-bisector' as const,
        },
        {
          description: 'all the points equidistant from two straight lines that cross',
          answer: 'the bisector of the angle between them',
          other: 'the perpendicular bisector of the segment joining them',
          figure: 'angle-bisector' as const,
        },
      ]);

      const { choices, correctId } = options(
        [
          { label: item.answer, correct: true },
          { label: item.other, misconceptionId: 'equidistant-locus-confused' },
          { label: 'a circle through both of them', misconceptionId: 'equidistant-locus-confused' },
          { label: 'the single point exactly halfway between them', misconceptionId: 'equidistant-locus-confused' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: `Which construction draws ${item.description}?`,
        figure: { kind: 'construction', construction: item.figure },
        choices,
        correctId,
        summary: `${item.answer.charAt(0).toUpperCase()}${item.answer.slice(1)}. Distance to two *points* gives the perpendicular bisector; distance to two *lines* gives the angle bisector.`,
        hint: 'There are infinitely many such points, not one. Ask what the two things being measured to are — points, or lines?',
      });
    },
  }),

  template({
    id: 'construction-triangle-centre',
    conceptId: 'constructions',
    description: 'Name the point where a set of constructed lines meets.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const item = rng.pick([
        {
          lines: 'the perpendicular bisectors of the three sides of a triangle',
          answer: 'the circumcentre — equidistant from the three vertices',
          other: 'the incentre — equidistant from the three sides',
          figure: 'perpendicular-bisector' as const,
        },
        {
          lines: 'the bisectors of the three angles of a triangle',
          answer: 'the incentre — equidistant from the three sides',
          other: 'the circumcentre — equidistant from the three vertices',
          figure: 'angle-bisector' as const,
        },
      ]);

      const { choices, correctId } = options(
        [
          { label: item.answer, correct: true },
          { label: item.other, misconceptionId: 'triangle-centre-confused' },
          { label: 'the midpoint of the longest side', misconceptionId: 'triangle-centre-confused' },
          { label: 'nowhere in particular — three lines rarely meet at one point', misconceptionId: 'measurement-taken-as-proof' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: `Where do ${item.lines} meet?`,
        figure: { kind: 'construction', construction: item.figure },
        choices,
        correctId,
        summary: `${item.answer}. Each construction is a set of equidistant points, so a point on two of them is equidistant from all three — which is exactly why the third line passes through it too.`,
        hint: 'Take a point on two of the three lines. What is it equidistant from, and what does that force about the third line?',
      });
    },
  }),

  template({
    id: 'construction-bisected-angle',
    conceptId: 'constructions',
    description: 'Find the angle left by one or two bisections.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const twice = difficulty === 3;
      const angle = twice ? rng.int(6, 44) * 4 : rng.int(6, 88) * 2;
      const answer = twice ? angle / 4 : angle / 2;

      return numericExercise({
        prompt: twice
          ? `An angle of ${angle}° is bisected, and one of the halves is bisected again. How big is the smallest angle now?`
          : `An angle of ${angle}° is bisected by construction. How big is each part?`,
        unit: 'degrees',
        figure: { kind: 'construction', construction: 'angle-bisector' },
        value: answer,
        summary: twice
          ? `Bisecting halves it to ${angle / 2}°, and bisecting again halves that: ${answer}°.`
          : `Bisecting cuts the angle into two equal parts: ${angle} ÷ 2 = ${answer}°.`,
        hint: 'To bisect is to cut into two equal parts — so each part is half of what you started with.',
        steps: twice
          ? [
              { label: 'Bisect once.', latex: `${angle} \\div 2 = ${angle / 2}` },
              { label: 'Bisect the half.', latex: `${angle / 2} \\div 2 = ${answer}` },
            ]
          : [{ label: 'Halve the angle.', latex: `${angle} \\div 2 = ${answer}` }],
        traps: [
          trap(angle, 'bisector-not-halved'),
          ...(twice ? [trap(angle / 2, 'bisector-not-halved')] : [trap(angle * 2, 'bisector-not-halved')]),
        ],
      });
    },
  }),

  template({
    id: 'construction-bisect-third-angle',
    conceptId: 'constructions',
    description: 'Find the third angle of a triangle, then bisect it.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const third = rng.int(10, 50) * 2;
      const first = rng.int(20, 180 - third - 20);
      const second = 180 - third - first;
      const answer = third / 2;

      return numericExercise({
        prompt: `A triangle has angles of ${first}° and ${second}°. Its third angle is bisected by construction. How big is each half?`,
        unit: 'degrees',
        figure: { kind: 'construction', construction: 'angle-bisector' },
        value: answer,
        summary: `The third angle is 180 − ${first} − ${second} = ${third}°, and bisecting it gives ${answer}°.`,
        hint: 'Find the third angle before you bisect anything.',
        steps: [
          { label: 'Use the angle sum of a triangle.', latex: `180 - ${first} - ${second} = ${third}` },
          { label: 'Bisect it.', latex: `${third} \\div 2 = ${answer}` },
        ],
        traps: [
          trap(third, 'bisector-not-halved'),
          ...((360 - first - second) / 2 === answer ? [] : [trap((360 - first - second) / 2, 'angle-sum-confused')]),
        ],
      });
    },
  }),
];
